import React from "react";
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Email as EmailIcon,
} from "@mui/icons-material";
import { TimestampDisplay } from "../../internal/components/TimestampDisplay";
import { ActionLogErrorIndicator, ActionLogFullErrorDisplay } from "./ActionLogErrorIndicator";
import { ActionLogActionButtons } from "./ActionLogActionButtons";
import { ActionLogInfo } from "@services/admin/types";

interface ActionLogTableRowProps {
  log: ActionLogInfo;
  includeDescription: boolean;
  displayErrors: boolean;
  onViewDetails: (log: ActionLogInfo) => void;
  onViewGptRequest: (gptRequestId: string) => void;
  onViewExtractedData: (extractedDataId: string) => void;
  onViewDerivedData: (derivedDataId: string) => void;
  onViewShipmentEmail: (shipmentEmailId: string) => void;
  onViewShipmentFeed: (shipmentFeedId: string) => void;
  onViewAIAgentTrace: (log: ActionLogInfo) => void;
  onViewErrors: (log: ActionLogInfo) => void;
  onViewEmail: (emailId: string) => void;
  onViewReminder?: (reminderId: string) => void;
}

const getTypeColor = (type: string) => {
  switch (type) {
    case "EmailSend":
      return "primary";
    case "GptRequest":
      return "secondary";
    case "CreateShipment":
    case "UpdateShipment":
      return "success";
    case "StartAIAgent":
      return "warning";
    case "EndAIAgent":
      return "success";
    default:
      return "default";
  }
};

export function ActionLogTableRow({
  log,
  includeDescription,
  displayErrors,
  onViewDetails,
  onViewGptRequest,
  onViewExtractedData,
  onViewDerivedData,
  onViewShipmentEmail,
  onViewShipmentFeed,
  onViewAIAgentTrace,
  onViewErrors,
  onViewEmail,
  onViewReminder,
}: ActionLogTableRowProps) {
  const hasExpandableContent = 
    (includeDescription && log.description) ||
    (displayErrors &&
      (log.extra?.errors ||
        log.extra?.error ||
        log.extra?.warnings ||
        log.extra?.warning ||
        log.extra?.infos ||
        log.extra?.info));

  return (
    <React.Fragment>
      <TableRow
        sx={
          hasExpandableContent
            ? { "& td": { borderBottom: "none" } }
            : {}
        }
      >
        <TableCell>
          <Chip
            label={log.type}
            size="small"
            color={getTypeColor(log.type) as any}
          />
        </TableCell>
        <TableCell
          sx={{ 
            maxWidth: "350px",
            minWidth: "200px",
            overflow: "hidden"
          }}
        >
          {log.email ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0
              }}
            >
              {log.emailId && (
                <Tooltip title="View Email Thread">
                  <IconButton
                    size="small"
                    onClick={() => onViewEmail(log.emailId!)}
                    sx={{ p: 0.5, flexShrink: 0 }}
                  >
                    <EmailIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={log.email.subject}
                >
                  {log.email.subject}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={log.email.fromEmail}
                >
                  {log.email.fromEmail}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No Email
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {log.user?.email || "Unknown user"}
          </Typography>
        </TableCell>
        <TableCell>
          <TimestampDisplay timestamp={log.createdAt} />
        </TableCell>
        <TableCell
          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {log.memo || "-"}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <ActionLogErrorIndicator
            log={log}
            onClick={onViewErrors ? () => onViewErrors(log) : undefined}
          />
        </TableCell>
        <TableCell align="center">
          <ActionLogActionButtons
            log={log}
            onViewDetails={onViewDetails}
            onViewGptRequest={onViewGptRequest}
            onViewExtractedData={onViewExtractedData}
            onViewDerivedData={onViewDerivedData}
            onViewShipmentEmail={onViewShipmentEmail}
            onViewShipmentFeed={onViewShipmentFeed}
            onViewAIAgentTrace={onViewAIAgentTrace}
            onViewErrors={onViewErrors}
            onViewReminder={onViewReminder}
            showErrorIndicator={false}
          />
        </TableCell>
      </TableRow>
      {includeDescription && log.description && (
        <TableRow>
          <TableCell
            colSpan={7}
            sx={{ py: 0, borderTop: "none", paddingBottom: 1 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontStyle: "italic",
                whiteSpace: "pre-line",
                pl: 2,
              }}
            >
              {log.description}
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {displayErrors &&
        (log.extra?.errors ||
          log.extra?.error ||
          log.extra?.warnings ||
          log.extra?.warning ||
          log.extra?.infos ||
          log.extra?.info) && (
          <TableRow>
            <TableCell
              colSpan={7}
              sx={{
                py: 0,
                borderTop: "none",
                paddingBottom: 1,
              }}
            >
              <ActionLogFullErrorDisplay log={log} />
            </TableCell>
          </TableRow>
        )}
    </React.Fragment>
  );
}